import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
import "./OrganigramaPublico.css";

function normalizar(valor) {
  return String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function ordenar(a, b) {
  const ao = Number(a?.orden ?? 999999);
  const bo = Number(b?.orden ?? 999999);
  if (ao !== bo) return ao - bo;
  return String(a?.titulo || "").localeCompare(String(b?.titulo || ""), "es");
}

function fotoBombero(bombero) {
  return bombero?.fotoURL || bombero?.fotoUrl || bombero?.foto || "";
}

function construirAsignaciones(nodos, bomberos) {
  const porId = new Map();
  bomberos.forEach((b) => {
    [b.id, b.rut, b.uid].filter(Boolean).forEach((k) => porId.set(String(k), b));
  });

  const porCargo = new Map();
  bomberos.forEach((b) => {
    const clave = normalizar(b.cargo);
    if (!clave) return;
    if (!porCargo.has(clave)) porCargo.set(clave, []);
    porCargo.get(clave).push(b);
  });
  porCargo.forEach((lista) => {
    lista.sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"));
  });

  const nodosMap = new Map(nodos.map((n) => [n.id, n]));
  const hijos = new Map();
  nodos.forEach((n) => {
    const p = n.parent_id || "__root__";
    if (!hijos.has(p)) hijos.set(p, []);
    hijos.get(p).push(n);
  });
  hijos.forEach((lista) => lista.sort(ordenar));

  // Devuelve la compañía a la que pertenece un nodo.
  // 1) Primero intenta reconocerla en el propio nombre del cargo
  //    (ej. "Director Primera Compañía").
  // 2) Si no aparece allí, sube por la jerarquía hasta encontrar un organismo
  //    que corresponda a una compañía (ej. Primera Compañía > Mando Operativo).
  const nombresCompania = new Map();
  bomberos.forEach((b) => {
    const nombre = String(b.compania || b.compañia || "").trim();
    if (nombre) nombresCompania.set(normalizar(nombre), nombre);
  });

  // Se ordenan por largo para evitar que una coincidencia parcial gane antes
  // que el nombre completo de la compañía.
  const companiasConocidas = Array.from(nombresCompania.entries()).sort(
    (a, b) => b[0].length - a[0].length
  );

  const numeroCompaniaDesdeNombre = (nombre) => {
    const t = normalizar(nombre);
    const palabras = {
      primera: 1, primer: 1, segunda: 2, segundo: 2, tercera: 3, tercer: 3,
      cuarta: 4, cuarto: 4, quinta: 5, quinto: 5, sexta: 6, sexto: 6,
      septima: 7, septimo: 7, octava: 8, octavo: 8, novena: 9, noveno: 9,
      decima: 10, decimo: 10,
    };

    for (const [palabra, numero] of Object.entries(palabras)) {
      if (new RegExp(`\\b${palabra}\\b`).test(t)) return numero;
    }

    // También admite nombres como "1ª Compañía", "2da Compañía", etc.
    const m = t.match(/(?:^|\s)(\d{1,2})(?:a|ra|da|ta)?\s*(?:cia|compania)\b/);
    return m ? Number(m[1]) : null;
  };

  const companiaPorNumero = new Map();
  nombresCompania.forEach((nombre) => {
    const numero = numeroCompaniaDesdeNombre(nombre);
    if (numero && !companiaPorNumero.has(numero)) companiaPorNumero.set(numero, nombre);
  });

  const companiaMencionadaEnTexto = (texto) => {
    const t = normalizar(texto);
    if (!t) return "";

    // 1) Nombre completo: "Primera Compañía", "Segunda Compañía", etc.
    const encontrada = companiasConocidas.find(([clave]) => t.includes(clave));
    if (encontrada) return encontrada[1];

    // 2) Abreviaturas habituales escritas JUNTO a Cía/Compañía.
    // Importante: esto evita confundir "Teniente 1°" con "1ra Cía".
    // Ejemplos soportados: 1ra Cía, 1ª Cía, 1a Compañía, 2da Cía, 3ra Cía...
    const abreviada = t.match(/(?:^|[\s\-–—])(?:cia\s*)?(\d{1,2})(?:a|ra|da|ta)?\s*(?:cia|compania)\b/);
    if (abreviada) {
      const numero = Number(abreviada[1]);
      if (companiaPorNumero.has(numero)) return companiaPorNumero.get(numero);
    }

    // 3) Formas "1ra", "2da" seguidas directamente de Cía, incluso con símbolos ordinales.
    const ordinal = t.match(/(?:^|[\s\-–—])(\d{1,2})\s*(?:ra|da|ta|a)?\s*(?:cia|compania)\b/);
    if (ordinal) {
      const numero = Number(ordinal[1]);
      if (companiaPorNumero.has(numero)) return companiaPorNumero.get(numero);
    }

    return "";
  };

  const companiaDeNodo = (nodo) => {
    // Caso importante para integrantes del Directorio General:
    // "Director Primera Compañía", "Director Segunda Compañía", etc.
    const explicita = companiaMencionadaEnTexto(nodo?.titulo);
    if (explicita) return explicita;

    let actual = nodo;
    const visitados = new Set();
    while (actual && !visitados.has(actual.id)) {
      visitados.add(actual.id);

      const exacta = nombresCompania.get(normalizar(actual.titulo));
      if (actual.tipo === "organismo" && exacta) return exacta;

      const mencionada = companiaMencionadaEnTexto(actual.titulo);
      if (mencionada) return mencionada;

      actual = actual.parent_id ? nodosMap.get(actual.parent_id) : null;
    }
    return "";
  };

  const asignaciones = new Map();
  const contexto = new Map();

  // Evita que varios nodos repetidos del MISMO cargo, compañía y misma rama
  // tomen siempre a la primera persona. A la vez permite que una misma persona
  // aparezca legítimamente en distintas partes del organigrama.
  // Ejemplo: el Director de Primera Compañía puede mostrarse tanto dentro de
  // Primera Compañía como también como integrante del Directorio General.
  const indicePorGrupo = new Map();

  const visitar = (nodo) => {
    let persona = null;
    const companiaRama = companiaDeNodo(nodo);

    if (nodo.tipo === "cargo") {
      if (nodo.asignacion_tipo === "persona" && nodo.bombero_id) {
        persona = porId.get(String(nodo.bombero_id)) || null;
      } else if (nodo.asignacion_tipo === "cargo" && nodo.cargo_busqueda) {
        const candidatosCargo = porCargo.get(normalizar(nodo.cargo_busqueda)) || [];

        // Si identificamos una compañía, la coincidencia es ESTRICTA:
        // cargo + compañía. Nunca usa un Director de otra compañía como reemplazo.
        const candidatos = companiaRama
          ? candidatosCargo.filter(
              (b) => normalizar(b.compania || b.compañia) === normalizar(companiaRama)
            )
          : candidatosCargo;

        // Reparte personas solamente cuando existen varios nodos equivalentes
        // dentro de la misma rama (por ejemplo dos Teniente 1° bajo el mismo padre).
        // En ramas distintas se permite reutilizar la misma persona.
        const grupo = [
          normalizar(nodo.cargo_busqueda),
          normalizar(companiaRama),
          String(nodo.parent_id || "__root__"),
        ].join("|");
        const indice = indicePorGrupo.get(grupo) || 0;
        persona = candidatos[indice] || null;
        indicePorGrupo.set(grupo, indice + 1);
      }
    }

    asignaciones.set(nodo.id, persona);
    contexto.set(nodo.id, { compania: companiaRama });
    (hijos.get(nodo.id) || []).forEach(visitar);
  };

  (hijos.get("__root__") || []).forEach(visitar);
  nodos.filter((n) => !asignaciones.has(n.id)).sort(ordenar).forEach(visitar);

  return { asignaciones, contexto };
}

function TarjetaCargo({ nodo, asignaciones, contexto }) {
  const persona = asignaciones.get(nodo.id) || null;
  const vacante = !persona;
  const companiaRama = contexto?.get(nodo.id)?.compania || "";

  // La compañía visible debe corresponder a la persona realmente asignada.
  // Esto es especialmente importante en asignación "Persona específica",
  // donde el nodo puede estar fuera de una rama de Compañía (por ejemplo,
  // dentro de Comandancia o Directorio General). Si el bombero tiene compañía,
  // se muestra esa; si no, se conserva la compañía inferida desde la jerarquía.
  const companiaPersona = persona
    ? String(persona.compania || persona.compañia || "").trim()
    : "";
  const companiaMostrada = companiaPersona || companiaRama;

  return (
    <div className="org-cargo-card">
      <div className="org-photo-wrap">
        {persona && fotoBombero(persona) ? (
          <img className="org-photo" src={fotoBombero(persona)} alt={persona.nombre || nodo.titulo} decoding="async" />
        ) : (
          <div className={`org-photo org-photo--empty ${vacante ? "is-vacant" : ""}`}>
            {vacante ? "VACANTE" : "SIN FOTO"}
          </div>
        )}
      </div>
      <div className="org-role-block">
        <div className="org-title">{nodo.titulo}</div>
        {nodo.mostrar_compania !== false && companiaMostrada && (
          <div className="org-company">{companiaMostrada}</div>
        )}
      </div>

      <div className="org-person-divider" />

      {persona ? (
        <div className="org-name">{persona.nombre || "Sin nombre"}</div>
      ) : (
        <div className="org-vacante">Cargo vacante</div>
      )}
      {nodo.descripcion && <div className="org-subtitle">{nodo.descripcion}</div>}
    </div>
  );
}

function NodoCargo({ nodo, hijosMap, asignaciones, contexto }) {
  const hijos = (hijosMap.get(nodo.id) || []).slice().sort(ordenar);

  return (
    <div className="org-branch">
      <TarjetaCargo nodo={nodo} asignaciones={asignaciones} contexto={contexto} />

      {hijos.length > 0 && (
        <div className="org-children">
          {hijos.map((hijo) => (
            <NodoOrganigrama
              key={hijo.id}
              nodo={hijo}
              hijosMap={hijosMap}
              asignaciones={asignaciones}
              contexto={contexto}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NodoOrganismo({ nodo, hijosMap, asignaciones, contexto }) {
  const hijos = (hijosMap.get(nodo.id) || []).slice().sort(ordenar);
  const cargosDirectos = hijos.filter((h) => h.tipo === "cargo");
  const organismosDirectos = hijos.filter((h) => h.tipo === "organismo");

  return (
    <div className="org-branch org-branch--organismo">
      <section className="org-organismo-box">
        <div className="org-organismo-header">
          <div className="org-organismo-icon">🏛️</div>
          <div>
            <div className="org-organismo-title">{nodo.titulo}</div>
            {nodo.descripcion && <div className="org-subtitle">{nodo.descripcion}</div>}
          </div>
        </div>

        {cargosDirectos.length > 0 && (
          <div className="org-organismo-cargos">
            {cargosDirectos.map((cargo) => (
              <NodoCargo
                key={cargo.id}
                nodo={cargo}
                hijosMap={hijosMap}
                asignaciones={asignaciones}
                contexto={contexto}
              />
            ))}
          </div>
        )}
      </section>

      {organismosDirectos.length > 0 && (
        <div className="org-children org-children--organismos">
          {organismosDirectos.map((hijo) => (
            <NodoOrganigrama
              key={hijo.id}
              nodo={hijo}
              hijosMap={hijosMap}
              asignaciones={asignaciones}
              contexto={contexto}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NodoOrganigrama({ nodo, hijosMap, asignaciones, contexto }) {
  if (nodo.tipo === "organismo") {
    return <NodoOrganismo nodo={nodo} hijosMap={hijosMap} asignaciones={asignaciones} contexto={contexto} />;
  }
  return <NodoCargo nodo={nodo} hijosMap={hijosMap} asignaciones={asignaciones} contexto={contexto} />;
}

export default function OrganigramaPublico() {
  const navigate = useNavigate();
  const { cuerpo, cuerpoId, cuerpoSlug } = useCuerpo();
  const [nodos, setNodos] = useState([]);
  const [bomberos, setBomberos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const treeScrollRef = useRef(null);
  const treeInnerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState(true);
  const [treeSize, setTreeSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!cuerpoId) return undefined;

    const cacheKey = `organigrama_publico_v2_${cuerpoId}`;
    let cache = null;

    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.nodos) && Array.isArray(parsed.bomberos)) {
          cache = parsed;
        }
      }
    } catch (e) {
      console.warn("No se pudo leer caché del organigrama", e);
    }

    // Si ya visitamos este organigrama antes, lo mostramos de inmediato.
    // Firestore queda escuchando en segundo plano y actualiza cualquier cambio.
    if (cache) {
      setNodos(cache.nodos);
      setBomberos(cache.bomberos);
      setCargando(false);
    } else {
      setNodos([]);
      setBomberos([]);
      setCargando(true);
    }
    setError("");

    let listoNodos = Boolean(cache);
    let listoBomberos = Boolean(cache);
    let nodosActuales = cache?.nodos || [];
    let bomberosActuales = cache?.bomberos || [];
    let huboRespuestaNodos = false;
    let huboRespuestaBomberos = false;

    const guardarCache = () => {
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            nodos: nodosActuales,
            bomberos: bomberosActuales,
            actualizadoEn: Date.now(),
          })
        );
      } catch (e) {
        console.warn("No se pudo guardar caché del organigrama", e);
      }
    };

    const terminar = () => {
      if ((listoNodos && listoBomberos) || (huboRespuestaNodos && huboRespuestaBomberos)) {
        setCargando(false);
      }
    };

    const unsubNodos = onSnapshot(
      query(collection(db, "organigrama_nodos"), where("cuerpo_id", "==", cuerpoId)),
      (snap) => {
        nodosActuales = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNodos(nodosActuales);
        listoNodos = true;
        huboRespuestaNodos = true;
        guardarCache();
        terminar();
      },
      (e) => {
        console.error(e);
        huboRespuestaNodos = true;
        if (!cache) setError("No se pudo cargar el organigrama.");
        terminar();
      }
    );

    const unsubBomberos = onSnapshot(
      query(collection(db, "bomberos"), where("cuerpo_id", "==", cuerpoId)),
      (snap) => {
        bomberosActuales = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBomberos(bomberosActuales);
        listoBomberos = true;
        huboRespuestaBomberos = true;
        guardarCache();
        terminar();
      },
      (e) => {
        console.error(e);
        huboRespuestaBomberos = true;
        if (!cache) setError("No se pudo cargar el personal del organigrama.");
        terminar();
      }
    );

    return () => {
      unsubNodos();
      unsubBomberos();
    };
  }, [cuerpoId]);

  const hijosMap = useMemo(() => {
    const mapa = new Map();
    nodos.forEach((n) => {
      const p = n.parent_id || "__root__";
      if (!mapa.has(p)) mapa.set(p, []);
      mapa.get(p).push(n);
    });
    mapa.forEach((lista) => lista.sort(ordenar));
    return mapa;
  }, [nodos]);

  const { asignaciones, contexto } = useMemo(() => construirAsignaciones(nodos, bomberos), [nodos, bomberos]);
  const raices = useMemo(() => (hijosMap.get("__root__") || []).slice().sort(ordenar), [hijosMap]);

  const centrarOrganigrama = (smooth = true) => {
    const el = treeScrollRef.current;
    if (!el) return;
    const destino = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
    el.scrollTo({ left: destino, behavior: smooth ? "smooth" : "auto" });
  };

  const ajustarAncho = () => {
    setFitMode(true);
  };

  const cambiarZoom = (delta) => {
    setFitMode(false);
    setZoom((actual) => Math.min(1.5, Math.max(0.35, Number((actual + delta).toFixed(2)))));
  };

  const zoom100 = () => {
    setFitMode(false);
    setZoom(1);
  };

  // Mide el ancho REAL del árbol. En escritorio puede reducirlo proporcionalmente
  // para que quepa dentro del viewport; en celular la CSS cambia a una jerarquía
  // vertical y se mantiene al 100% para no achicar textos ni tarjetas.
  useEffect(() => {
    if (cargando || error || raices.length === 0) return undefined;

    const viewport = treeScrollRef.current;
    const tree = treeInnerRef.current;
    if (!viewport || !tree) return undefined;

    const recalcular = () => {
      const width = Math.max(tree.scrollWidth, tree.offsetWidth, 1);
      const height = Math.max(tree.scrollHeight, tree.offsetHeight, 1);
      setTreeSize({ width, height });

      // En cualquier tamaño de pantalla (incluido celular) el organigrama
      // conserva su estructura horizontal completa y se reduce como una sola
      // pieza hasta caber dentro del ancho disponible. Así nunca se corta ni
      // cambia la jerarquía visual. Luego el usuario puede acercar con los
      // controles o con el zoom/pinch del navegador.
      if (fitMode) {
        const esMovil = window.matchMedia("(max-width: 820px)").matches;
        const margen = esMovil ? 4 : 20;
        const disponible = Math.max(1, viewport.clientWidth - margen);
        const escala = Math.min(1, Math.max(0.06, disponible / width));
        setZoom(Number(escala.toFixed(3)));
      }
    };

    recalcular();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recalcular) : null;
    ro?.observe(tree);
    ro?.observe(viewport);
    window.addEventListener("resize", recalcular);

    const timers = [
      setTimeout(recalcular, 120),
      setTimeout(recalcular, 450),
      setTimeout(recalcular, 900),
    ];

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", recalcular);
      timers.forEach(clearTimeout);
    };
  }, [cargando, error, raices.length, fitMode]);

  // Centrado automático después de calcular el tamaño/zoom.
  useEffect(() => {
    if (cargando || error || raices.length === 0) return undefined;
    const raf = requestAnimationFrame(() => centrarOrganigrama(false));
    const timer = setTimeout(() => centrarOrganigrama(false), 180);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [cargando, error, raices.length, zoom, treeSize.width]);

  return (
    <div className="org-page">
      <div className="org-toolbar">
        <button onClick={() => navigate(`/${cuerpoSlug}`)}>← Volver</button>
        {!cargando && !error && raices.length > 0 && (
          <>
            <button onClick={ajustarAncho}>↔ Ajustar al ancho</button>
            <button onClick={() => cambiarZoom(-0.1)} aria-label="Alejar organigrama">−</button>
            <button onClick={zoom100}>100%</button>
            <button onClick={() => cambiarZoom(0.1)} aria-label="Acercar organigrama">+</button>
            <button onClick={() => centrarOrganigrama(true)}>◎ Centrar</button>
          </>
        )}
      </div>

      <header className="org-header">
        {cuerpo?.logoURL && <img src={cuerpo.logoURL} alt="Logo del Cuerpo" className="org-cuerpo-logo" />}
        <div>
          <h1>Organigrama</h1>
          <p>{cuerpo?.nombre || "Cuerpo de Bomberos"}</p>
        </div>
      </header>

      {cargando && <div className="org-message">Cargando organigrama...</div>}
      {!cargando && error && <div className="org-message org-message--error">{error}</div>}
      {!cargando && !error && raices.length === 0 && (
        <div className="org-message">El organigrama todavía no ha sido configurado.</div>
      )}

      {!cargando && !error && raices.length > 0 && (
        <div className="org-tree-scroll" ref={treeScrollRef}>
          <div
            className="org-tree-stage"
            style={{
              width: treeSize.width ? `${Math.ceil(treeSize.width * zoom)}px` : undefined,
              height: treeSize.height ? `${Math.ceil(treeSize.height * zoom)}px` : undefined,
            }}
          >
            <div
              className="org-tree"
              ref={treeInnerRef}
              style={{ transform: `scale(${zoom})` }}
            >
              {raices.map((nodo) => (
                <NodoOrganigrama
                  key={nodo.id}
                  nodo={nodo}
                  hijosMap={hijosMap}
                  asignaciones={asignaciones}
                  contexto={contexto}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
